import React from 'react';

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 3000;

interface UseReconnectOptions {
  isReconnectingRef: React.MutableRefObject<boolean>;
  reconnectAttemptsRef: React.MutableRefObject<number>;
  setIsReconnecting: (val: boolean) => void;
  setReconnectAttempts: (val: number | ((prev: number) => number)) => void;
  onStop: () => void;
  onStart: () => Promise<void>;
}

export const useReconnect = (options: UseReconnectOptions) => {
  const {
    isReconnectingRef,
    reconnectAttemptsRef,
    setIsReconnecting,
    setReconnectAttempts,
    onStop,
    onStart,
  } = options;

  const reconnectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const reconnectRef = React.useRef<() => Promise<void>>();

  reconnectRef.current = async () => {
    if (isReconnectingRef.current || reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.error('Max reconnection attempts reached or already reconnecting');
      return;
    }

    setIsReconnecting(true);
    setReconnectAttempts(prev => prev + 1);

    try {
      onStop();
      await new Promise(resolve => setTimeout(resolve, RECONNECT_DELAY));
      await onStart();
      setReconnectAttempts(0);
      setIsReconnecting(false);
    } catch (error) {
      console.error('Reconnection attempt failed:', error);
      setIsReconnecting(false);

      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectTimeoutRef.current = setTimeout(() => reconnectRef.current?.(), RECONNECT_DELAY);
      }
    }
  };

  const reconnect = React.useCallback(async () => {
    reconnectRef.current?.();
  }, []);

  const clearReconnectTimeout = React.useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
  }, []);

  return { reconnect, clearReconnectTimeout };
};
